import React, { createContext, useContext, useState, useEffect } from 'react'
import enTranslations from '../locales/en.json'
import nlTranslations from '../locales/nl.json'

type Locale = 'en' | 'nl'

interface LocaleContextType {
  locale: Locale
  setLocale: (locale: Locale) => void
  t: (key: string) => any
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined)

export const useLocale = () => {
  const context = useContext(LocaleContext)
  if (context === undefined) {
    throw new Error('useLocale must be used within a LocaleProvider')
  }
  return context
}

interface LocaleProviderProps {
  children: React.ReactNode
}

const TRANSLATIONS: Record<Locale, Record<string, any>> = {
  en: enTranslations,
  nl: nlTranslations,
}

/** Dutch is the default: RallyShift's customers are Dutch events. */
function initialLocale(): Locale {
  return navigator.language?.toLowerCase().startsWith('en') ? 'en' : 'nl'
}

export const LocaleProvider: React.FC<LocaleProviderProps> = ({ children }) => {
  const [locale, setLocale] = useState<Locale>(initialLocale)

  useEffect(() => {
    document.documentElement.lang = locale
  }, [locale])

  const t = (key: string): any => {
    let value: any = TRANSLATIONS[locale]

    for (const k of key.split('.')) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k]
      } else {
        console.warn(`Translation not found for key: ${key}`)
        return key
      }
    }

    return value
  }

  const value = {
    locale,
    setLocale,
    t
  }

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
}
